/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Shopify Product Data Bridge
 * ============================================================
 *
 * Requires:
 * 1. rise-skin-products.js
 * 2. rise-skin-results.js
 *
 * This file:
 * - Fetches live Shopify product data by handle
 * - Enriches product objects with current price/image/variant
 * - Caches responses
 * - Refreshes visible routine cards
 * ============================================================
 */

window.RISE_SKIN = window.RISE_SKIN || {};


// ============================================================
// CACHE
// ============================================================

window.RISE_SKIN.SHOPIFY_CACHE =
  window.RISE_SKIN.SHOPIFY_CACHE || {};


// ============================================================
// GET PRODUCT HANDLE
// ============================================================

window.RISE_SKIN.getShopifyHandle =
  function (product) {

    return (
      product?.commerce?.productHandle ||
      null
    );
  };


// ============================================================
// FETCH SHOPIFY PRODUCT JSON
// ============================================================

window.RISE_SKIN.fetchShopifyProduct =
  async function (handle) {

    if (!handle) {
      return null;
    }


    if (
      window.RISE_SKIN
        .SHOPIFY_CACHE[handle]
    ) {
      return window.RISE_SKIN
        .SHOPIFY_CACHE[handle];
    }


    try {

      const response =
        await fetch(
          `/products/${handle}.js`,
          {
            headers: {
              Accept:
                "application/json"
            }
          }
        );


      if (!response.ok) {

        throw new Error(
          `Unable to load Shopify product: ${handle}`
        );
      }


      const product =
        await response.json();


      window.RISE_SKIN
        .SHOPIFY_CACHE[handle] =
        product;


      return product;

    } catch (error) {

      console.warn(
        "RISE SKIN Shopify fetch warning:",
        handle,
        error
      );


      return null;
    }
  };


// ============================================================
// GET FIRST AVAILABLE VARIANT
// ============================================================

window.RISE_SKIN.getFirstAvailableVariant =
  function (shopifyProduct) {

    const variants =
      Array.isArray(
        shopifyProduct?.variants
      )
        ? shopifyProduct.variants
        : [];


    const available =
      variants.find(
        (variant) =>
          variant.available !== false
      );


    return (
      available ||
      variants[0] ||
      null
    );
  };


// ============================================================
// NORMALIZE SHOPIFY PRICE
// ============================================================

window.RISE_SKIN.normalizeShopifyPrice =
  function (value) {

    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return null;
    }


    const numeric =
      Number(value);


    if (Number.isNaN(numeric)) {
      return null;
    }


    /*
     * Shopify product.js commonly returns
     * price in cents.
     */
    return numeric / 100;
  };


// ============================================================
// GET SHOPIFY PRODUCT IMAGE
// ============================================================

window.RISE_SKIN.getShopifyProductImage =
  function (shopifyProduct) {

    if (
      Array.isArray(
        shopifyProduct?.images
      ) &&
      shopifyProduct.images.length
    ) {
      return shopifyProduct.images[0];
    }


    if (
      shopifyProduct?.featured_image
    ) {
      return shopifyProduct.featured_image;
    }


    return null;
  };


// ============================================================
// ENRICH ONE PRODUCT OBJECT
// ============================================================

window.RISE_SKIN.enrichProductFromShopify =
  async function (product) {

    if (!product) {
      return product;
    }


    const handle =
      window.RISE_SKIN
        .getShopifyHandle(
          product
        );


    if (!handle) {
      return product;
    }


    const shopifyProduct =
      await window.RISE_SKIN
        .fetchShopifyProduct(
          handle
        );


    if (!shopifyProduct) {
      return product;
    }


    const variant =
      window.RISE_SKIN
        .getFirstAvailableVariant(
          shopifyProduct
        );


    const image =
      window.RISE_SKIN
        .getShopifyProductImage(
          shopifyProduct
        );


    const price =
      window.RISE_SKIN
        .normalizeShopifyPrice(
          variant?.price ??
          shopifyProduct?.price
        );


    // --------------------------------------------------------
    // ENRICH IN MEMORY
    // --------------------------------------------------------

    if (image) {
      product.imageUrl = image;
    }


    if (price !== null) {
      product.price = price;
    }


    product.shopifyTitle =
      shopifyProduct.title ||
      product.name;


    product.shopifyUrl =
      `/products/${handle}`;


    product.commerce =
      product.commerce || {};


    product.commerce.productHandle =
      handle;


    if (variant?.id) {
      product.commerce.variantId =
        String(variant.id);
    }


    if (shopifyProduct?.id) {
      product.commerce.productId =
        String(
          shopifyProduct.id
        );
    }


    return product;
  };


// ============================================================
// COLLECT UNIQUE PRODUCTS FROM RESULTS
// ============================================================

window.RISE_SKIN.getResultsProducts =
  function (result) {

    if (
      !result ||
      !result.routines
    ) {
      return [];
    }


    const map =
      new Map();


    [
      "simple",
      "complete",
      "elevated"
    ].forEach(
      (level) => {

        const routine =
          result.routines[level];


        if (!routine) {
          return;
        }


        [
          ...routine.am,
          ...routine.pm
        ].forEach(
          (item) => {

            const product =
              item.product;


            if (!product) {
              return;
            }


            const id =
              window.RISE_SKIN
                .getProductId(
                  product
                );


            if (!map.has(id)) {
              map.set(
                id,
                product
              );
            }
          }
        );
      }
    );


    return Array.from(
      map.values()
    );
  };


// ============================================================
// ENRICH ALL PRODUCTS USED IN RESULTS
// ============================================================

window.RISE_SKIN.enrichResultsProducts =
  async function (result) {

    const products =
      window.RISE_SKIN
        .getResultsProducts(
          result
        );


    const enrichable =
      products.filter(
        (product) =>
          Boolean(
            window.RISE_SKIN
              .getShopifyHandle(
                product
              )
          )
      );


    if (
      window.RISE_DEBUG
    ) {

      console.log(
        `RISE SKIN Shopify bridge: enriching ${enrichable.length} products`
      );
    }


    await Promise.all(
      enrichable.map(
        (product) =>
          window.RISE_SKIN
            .enrichProductFromShopify(
              product
            )
      )
    );


    return result;
  };


// ============================================================
// REFRESH CURRENT RESULTS
// ============================================================

window.RISE_SKIN.refreshResultsAfterShopify =
  function () {

    const result =
      window.RISE_SKIN
        .RESULTS?.data;


    if (!result) {
      return;
    }


    const activeLevel =
      window.RISE_SKIN
        .RESULTS
        .activeLevel ||
      result.recommendedLevel ||
      "complete";


    window.RISE_SKIN
      .renderRoutineLevel(
        activeLevel
      );


    // Re-populate headline in case
    // commerce metadata affects display later.
    const headline =
      window.RISE_SKIN
        .getHeadlineProduct(
          result
        );


    const headlineName =
      document.getElementById(
        "rsc-headline-product"
      );


    if (
      headline &&
      headlineName
    ) {

      headlineName.textContent =
        headline.product.name;
    }
  };


// ============================================================
// RESULTS HOOK
// ============================================================

window.RISE_SKIN.enrichAndRefreshResults =
  async function (result) {

    await window.RISE_SKIN
      .enrichResultsProducts(
        result
      );


    window.RISE_SKIN
      .refreshResultsAfterShopify();


    if (window.RISE_DEBUG) {

      console.log(
        "RISE SKIN Shopify bridge: results refreshed"
      );
    }


    return result;
  };


// ============================================================
// LOAD-ORDER-SAFE QUIZ COMPLETE BRIDGE
// ============================================================

window.RISE_SKIN.installShopifyQuizCompleteBridge =
  function () {

    const current =
      window.RISE_SKIN
        .onQuizComplete;


    // Results renderer has not defined completion yet.
    // Return false so the installer can try again shortly.
    if (typeof current !== "function") {
      return false;
    }


    // Avoid wrapping the same flow more than once.
    if (current._riseShopifyWrapped) {
      return true;
    }


    const previousOnQuizComplete =
      current;


    const wrappedOnQuizComplete =
      function (profile) {

        // Always let the existing Rise results/lead-gate flow run first.
        previousOnQuizComplete(
          profile
        );


        // Wait until RESULTS.data exists, then enrich the chosen
        // products with live Shopify price/image/variant data.
        window.setTimeout(
          async function () {

            const result =
              window.RISE_SKIN
                .RESULTS?.data;


            if (!result) {
              return;
            }


            await window.RISE_SKIN
              .enrichAndRefreshResults(
                result
              );

          },
          1100
        );
      };


    wrappedOnQuizComplete
      ._riseShopifyWrapped =
      true;


    window.RISE_SKIN
      .onQuizCompleteBeforeShopify =
      previousOnQuizComplete;


    window.RISE_SKIN
      .onQuizComplete =
      wrappedOnQuizComplete;


    if (window.RISE_DEBUG) {
      console.log(
        "RISE SKIN Shopify bridge: quiz completion hook installed"
      );
    }


    return true;
  };


window.RISE_SKIN.initializeShopifyQuizCompleteBridge =
  function () {

    let attempts = 0;

    const maxAttempts = 200;


    const tryInstall =
      function () {

        attempts += 1;


        if (
          window.RISE_SKIN
            .installShopifyQuizCompleteBridge()
        ) {
          return;
        }


        if (attempts >= maxAttempts) {

          console.error(
            "RISE SKIN: Results completion function was not available for Shopify bridge."
          );

          return;
        }


        window.setTimeout(
          tryInstall,
          50
        );
      };


    tryInstall();
  };


window.RISE_SKIN
  .initializeShopifyQuizCompleteBridge();


// ============================================================
// MANUAL DEBUG
// ============================================================

window.RISE_SKIN.debugShopifyBridge =
  async function () {

    const result =
      window.RISE_SKIN
        .RESULTS?.data;


    if (!result) {

      console.warn(
        "RISE SKIN: no results data available."
      );

      return null;
    }


    await window.RISE_SKIN
      .enrichAndRefreshResults(
        result
      );


    const products =
      window.RISE_SKIN
        .getResultsProducts(
          result
        );


    const table =
      products.map(
        (product) => ({
          product:
            product.name,

          handle:
            product.commerce
              ?.productHandle ||
            null,

          variant:
            product.commerce
              ?.variantId ||
            null,

          price:
            product.price ||
            null,

          image:
            Boolean(
              product.imageUrl
            )
        })
      );


    console.table(table);


    return products;
  };


// ============================================================
// INITIAL CHECK
// ============================================================

if (window.RISE_DEBUG) {

  console.log(
    "RISE SKIN CONCIERGE — Shopify Product Bridge loaded"
  );

}